/**
 * Date utility functions for handling Bolivia timezone (UTC-4)
 */

/**
 * Converts a date string (yyyy-mm-dd) to UTC timestamp with Bolivia timezone offset
 * For start of day: 00:00:00 BOT = 04:00:00 UTC
 * @param dateString - Date in yyyy-mm-dd format
 * @returns ISO 8601 timestamp in UTC
 */
export function convertBoliviaDateToUTCStart(dateString: string): string {
  if (!dateString) return "";
  
  // Parse the date components
  const [year, month, day] = dateString.split("-").map(Number);
  
  // Create a date object in Bolivia time (UTC-4)
  // Set to 00:00:00 local time
  const boliviaDate = new Date(year, month - 1, day, 0, 0, 0, 0);
  
  // Add 4 hours to convert Bolivia time to UTC
  const utcDate = new Date(boliviaDate.getTime() + (4 * 60 * 60 * 1000));
  
  return utcDate.toISOString();
}

/**
 * Converts a date string (yyyy-mm-dd) to UTC timestamp with Bolivia timezone offset
 * For end of day: 23:59:59 BOT = 03:59:59 UTC (next day)
 * @param dateString - Date in yyyy-mm-dd format
 * @returns ISO 8601 timestamp in UTC
 */
export function convertBoliviaDateToUTCEnd(dateString: string): string {
  if (!dateString) return "";
  
  // Parse the date components
  const [year, month, day] = dateString.split("-").map(Number);
  
  // Create a date object in Bolivia time (UTC-4)
  // Set to 23:59:59 local time
  const boliviaDate = new Date(year, month - 1, day, 23, 59, 59, 999);
  
  // Add 4 hours to convert Bolivia time to UTC
  const utcDate = new Date(boliviaDate.getTime() + (4 * 60 * 60 * 1000));
  
  return utcDate.toISOString();
}

/**
 * Formats a date for display in Bolivia format (dd/mm/yyyy)
 * @param isoDate - Date in yyyy-mm-dd format
 * @returns Date in dd/mm/yyyy format
 */
export function formatDateBolivia(isoDate: string): string {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  return `${day}/${month}/${year}`;
}

/**
 * Parses a Bolivia format date (dd/mm/yyyy) to ISO format (yyyy-mm-dd)
 * @param boliviaDate - Date in dd/mm/yyyy format
 * @returns Date in yyyy-mm-dd format
 */
export function parseBoliviaDate(boliviaDate: string): string {
  if (!boliviaDate) return "";
  const [day, month, year] = boliviaDate.split("/");
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

/** Years offered by year filters: 2021 through next year, newest first. */
export function getAvailableYears(currentYear: number = new Date().getFullYear()): number[] {
  return Array.from({ length: currentYear - 2020 + 2 }, (_, i) => 2021 + i).reverse();
}
